import { motion } from "framer-motion";
import { useAtom } from "jotai";
import { FC, Fragment } from "react";
import ReactFocusLock from "react-focus-lock";

import { AccountIcon, CloseIcon, ManscapedLogo, MenuIcon } from "@theme/assets";
import {
  Container,
  Flex,
  IconButton,
  NavAccountDropdown,
  NavCartLink,
  NavLangDropdown,
  NavLink,
  NavMobileDrawer,
  Spacer,
  Text,
  getThemeValue,
} from "@theme/components";
import { drawerOpenAtom } from "@theme/components/Navigation";
import { NavigationDataProps } from "@theme/types";

interface NavMenuBarProps {
  navData: NavigationDataProps;
  isCheckout?: boolean;
}

const desktopOnly = { display: ["none", null, null, "flex"] };

export const NavMenuBar: FC<NavMenuBarProps> = ({ navData, isCheckout }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useAtom(drawerOpenAtom);

  return (
    <motion.nav
      initial="show"
      sx={{
        bg: "grey700",
        width: "full",
        zIndex: 2,
      }}
    >
      <Container
        variant="wide"
        as="menu"
        sx={{
          height: (t) => getThemeValue(t, "sizes.navBarHeight"),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
        }}
      >
        {!isCheckout && (
          // returnFocus must be false because it causes the focus to go to the skip to main content button
          <ReactFocusLock disabled={!isDrawerOpen} returnFocus={false}>
            <IconButton
              data-testid="button-menu-testid-2"
              aria-label="Toggle mobile menu"
              aria-expanded={isDrawerOpen}
              variant="iconDark"
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              sx={{
                display: ["flex", null, null, "none"],
                zIndex: 1,
                transform: (t) => `translateX(-${getThemeValue(t, "space.s200")})`,
                padding: 0,
                ":hover": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {isDrawerOpen ? (
                <CloseIcon
                  sx={{ color: "white !important", width: "iconSizeBase", height: "auto" }}
                />
              ) : (
                <MenuIcon
                  sx={{ color: "white !important", width: "iconSizeBase", height: "auto" }}
                />
              )}
            </IconButton>
            <NavMobileDrawer
              navCategories={navData.siteNavData?.mobileCategories}
              additionalLinks={navData.additionalLinks}
              loginLink={navData.loginLink}
              accountAuthenticatedLink={navData.accountAuthenticatedLink}
              blogLink={navData.blogLink}
              isAuthenticated={navData.isAuthenticated}
            />
          </ReactFocusLock>
        )}
        <NavLink
          variant="navbarItem"
          to={navData.homeUrl}
          aria-label="Go to Homepage"
          sx={{ mr: "auto" }}
        >
          <ManscapedLogo
            sx={{
              color: "white",
              width: [100, 150, null, null],
              height: [18, 27, null, null],
              padding: 0,
            }}
          />
        </NavLink>
        {!isCheckout && (
          <Flex
            sx={{
              height: "full",
              alignItems: "center",
              mr: (t) => `-${getThemeValue(t, "space.s200")}`,
            }}
          >
            {navData.additionalLinks.map((link) => (
              <Fragment key={link.label}>
                <NavLink to={link.to} variant="navbarItem" sx={{ ...desktopOnly }}>
                  <Text>{link.label}</Text>
                </NavLink>
                <Spacer
                  direction="horizontal"
                  space={["s200", "s300", "s600", "s700"]}
                  sx={{ display: ["none", null, null, "flex"] }}
                />
              </Fragment>
            ))}
            {navData.languages.length > 1 && (
              <>
                <NavLangDropdown
                  languages={navData.languages}
                  currentLanguage={navData.currentLanguage}
                  originalPath={navData.originalPath}
                />
                <Spacer
                  direction="horizontal"
                  space={["s200", "s300", "s600", "s700"]}
                  sx={{ display: "flex" }}
                />
              </>
            )}
            {navData.isAuthenticated ? (
              <NavAccountDropdown
                accountLabel={navData.accountLabel}
                accountLinks={navData.accountLinks}
                signOut={navData.signOut}
                sx={{ ...desktopOnly }}
              />
            ) : (
              <NavLink
                to={navData.loginLink.to}
                variant="navbarItem"
                data-testid="nav-login-link"
                sx={{ ...desktopOnly }}
                aria-label={navData.loginLink.label}
              >
                <AccountIcon sx={{ color: "grey300" }} />
              </NavLink>
            )}
            <Spacer
              direction="horizontal"
              space={["s200", "s300", "s600", "s700"]}
              sx={{ display: ["none", null, null, "flex"] }}
            />

            <NavCartLink navData={navData} />
          </Flex>
        )}
      </Container>
    </motion.nav>
  );
};
